import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const handleClose = () => {
    // Remove URL parameters and navigate to home
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stripe/session-status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to fetch status');
        
        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[var(--vp-paper)] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        {status === 'loading' && (
          <>
            <div className="mb-6 flex justify-center">
              <Loader2 className="w-16 h-16 text-[var(--vp-pink)] animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold text-[var(--vp-ink)] mb-4">
              Ověřujeme platbu...
            </h1>
            <p className="text-[var(--vp-ink)]/70">
              Prosím vyčkejte, kontrolujeme stav vaší platby.
            </p>
          </>
        )}

        {status === 'success' && (
  <>
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="mb-6 flex justify-center"
    >
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-12 h-12 text-green-600" />
      </div>
    </motion.div>

    <h1 className="text-3xl font-semibold text-[var(--vp-ink)] mb-4">
      🎉 Platba proběhla úspěšně!
    </h1>

    <p className="text-[var(--vp-ink)]/70 mb-4">
      Vaše objednávka byla úspěšně dokončena.
    </p>

    <div className="bg-[var(--vp-sand)]/40 border border-[var(--vp-line)] rounded-xl p-4 mb-6 text-left">
      <p className="text-sm text-[var(--vp-ink)]/80 mb-2">
        ✔ Výzva byla aktivována
      </p>
      <p className="text-sm text-[var(--vp-ink)]/80 mb-2">
        ✔ Potvrzení jsme odeslali na váš e-mail
      </p>
      <p className="text-sm text-[var(--vp-ink)]/80">
        ✔ SMS výzvy začnou chodit podle nastavení
      </p>
    </div>

    <p className="text-[var(--vp-ink)]/60 mb-8 text-sm">
      Pokud by vám e-mail nepřišel do několika minut, zkontrolujte složku spam.
    </p>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link to="/">
        <Button className="btn-primary px-6 py-3" data-testid="success-back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na hlavní stránku
        </Button>
      </Link>
      <Button 
        onClick={handleClose}
        variant="outline"
        className="px-6 py-3 border-[var(--vp-line)]"
        data-testid="success-close-button"
      >
        <X className="w-4 h-4 mr-2" />
        Zavřít
      </Button>
    </div>
  </>
)}

        {status === 'pending' && (
          <>
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-[var(--vp-sand)] rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[var(--vp-pink)]" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-[var(--vp-ink)] mb-4">
              Platba se zpracovává
            </h1>
            <p className="text-[var(--vp-ink)]/70 mb-8">
              Vaše platba je stále zpracovávána. Zkontrolujte prosím váš e-mail pro potvrzení.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button className="btn-primary px-6 py-3">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět na hlavní stránku
                </Button>
              </Link>
              <Button 
                onClick={handleClose}
                variant="outline"
                className="px-6 py-3 border-[var(--vp-line)]"
                data-testid="pending-close-button"
              >
                <X className="w-4 h-4 mr-2" />
                Zavřít
              </Button>
            </div>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-[var(--vp-ink)] mb-4">
              {status === 'expired' ? 'Platba vypršela' : 'Něco se pokazilo'}
            </h1>
            <p className="text-[var(--vp-ink)]/70 mb-8">
              {status === 'expired'
                ? 'Vaše platební relace vypršela. Zkuste to prosím znovu.'
                : 'Nepodařilo se ověřit stav platby. Kontaktujte nás prosím.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button className="btn-primary px-6 py-3">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zkusit znovu
                </Button>
              </Link>
              <Button 
                onClick={handleClose}
                variant="outline"
                className="px-6 py-3 border-[var(--vp-line)]"
                data-testid="error-close-button"
              >
                <X className="w-4 h-4 mr-2" />
                Zavřít
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SuccessPage;
